/**
 * useUrlState Hook
 *
 * A custom React hook that synchronizes state with URL hash parameters.
 * Values are compressed using LZ-string to keep URLs manageable while
 * allowing complex state to be shared via URL.
 *
 * Use cases:
 * - Shareable application state via URL
 * - Preserving form state across page refreshes
 * - Deep linking to specific application states
 */
import { useEffect, useState } from 'react';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';

/** Parses the URL hash fragment into URLSearchParams */
const getHashParams = () => new URLSearchParams(window.location.hash.slice(1));

/** Updates the URL hash without triggering navigation */
const setHashParams = (params: URLSearchParams) => {
  const hash = params.toString();
  const newUrl = `${window.location.pathname}${window.location.search}${hash ? '#' + hash : ''}`;
  window.history.replaceState({}, '', newUrl);
};

/**
 * Synchronizes a state value with a URL hash parameter.
 *
 * @param key - The parameter name in the URL hash
 * @param defaultValue - Value to use when parameter is not present in URL
 * @returns Tuple of [currentValue, setValue] similar to useState
 *
 * @example
 * const [schema, setSchema] = useUrlState('schema', '{}');
 */
export const useUrlState = (key: string, defaultValue: string): [string, (value: string) => void] => {
  const getUrlValue = () => {
    const compressed = getHashParams().get(key);
    if (!compressed) return defaultValue;
    const decompressed = decompressFromEncodedURIComponent(compressed);
    return decompressed ?? defaultValue;
  };

  const [value, setValue] = useState(getUrlValue());

  // Initialize URL with default value if parameter doesn't exist
  useEffect(() => {
    const params = getHashParams();
    if (!params.has(key) && defaultValue) {
      params.set(key, compressToEncodedURIComponent(defaultValue));
      setHashParams(params);
    }
  }, [key, defaultValue]);

  const setUrlValue = (newValue: string) => {
    setValue(newValue);
    const params = getHashParams();
    if (newValue) {
      params.set(key, compressToEncodedURIComponent(newValue));
    } else {
      params.delete(key);
    }
    setHashParams(params);
  };

  return [value, setUrlValue];
};
